import React, { useEffect, useMemo, useState } from "react";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import { Alert, Badge, Button, Card, Col, Container, Form, ListGroup, Row, Spinner } from "react-bootstrap";
import "./assetbookingsadmin.css";
import {
    approveAssetBooking,
    getAdminAssetBookings,
    rejectAssetBooking,
} from "../../api/requests";

const statusLabel = {
    pending: "Ausstehend",
    approved: "Freigegeben",
    rejected: "Abgelehnt",
};

const statusVariant = {
    pending: "warning",
    approved: "success",
    rejected: "danger",
};

const monthRange = (date) => {
    const start = new Date(date.getFullYear(), date.getMonth(), 1, 0, 0, 0, 0);
    const end = new Date(date.getFullYear(), date.getMonth() + 1, 1, 0, 0, 0, 0);
    return { start, end };
};

const bookingTouchesDate = (booking, date) => {
    const start = new Date(booking.start_datetime);
    const end = new Date(booking.end_datetime);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return false;
    const dayStart = new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0);
    const dayEnd = new Date(date.getFullYear(), date.getMonth(), date.getDate() + 1, 0, 0, 0, 0);
    return start < dayEnd && end > dayStart;
};

const formatDate = (dateTimeStr) => {
    if (!dateTimeStr) return "";
    const d = new Date(dateTimeStr);
    if (Number.isNaN(d.getTime())) return String(dateTimeStr);
    return d.toLocaleDateString("de-DE", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
    });
};

const formatDateTime = (dateTimeStr) => {
    if (!dateTimeStr) return "";
    const d = new Date(dateTimeStr);
    if (Number.isNaN(d.getTime())) return String(dateTimeStr);
    return d.toLocaleString("de-DE", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    });
};

const formatMonth = (date) => (
    date.toLocaleDateString("de-DE", {
        month: "long",
        year: "numeric",
    })
);

const assetColor = (assetId) => {
    const colors = ["#0b6bcb", "#0f8b6f", "#c25a1e", "#7c3aed", "#b91c1c", "#2563eb"];
    const value = Number(assetId) || 0;
    return colors[value % colors.length];
};

const errorText = (error) => {
    const detail = error?.response?.data?.detail;
    if (typeof detail === "string") return detail;
    return "Laden/Aktion fehlgeschlagen.";
};

const AssetBookingsAdmin = () => {
    const [q, setQ] = useState("");
    const [status, setStatus] = useState("pending");
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [workingId, setWorkingId] = useState(null);
    const [rejectNotes, setRejectNotes] = useState({});
    const [overviewDate, setOverviewDate] = useState(new Date());
    const [selectedOverviewDate, setSelectedOverviewDate] = useState(new Date());
    const [overviewBookings, setOverviewBookings] = useState([]);
    const [overviewLoading, setOverviewLoading] = useState(false);
    const [overviewError, setOverviewError] = useState(null);

    const load = async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await getAdminAssetBookings({
                q,
                status: status === "all" ? undefined : status,
            });
            setItems(Array.isArray(res) ? res : []);
        } catch (e) {
            console.error(e);
            setError(e);
            setItems([]);
        } finally {
            setLoading(false);
        }
    };

    const loadOverview = async (date = overviewDate) => {
        setOverviewLoading(true);
        setOverviewError(null);
        try {
            const range = monthRange(date);
            const res = await getAdminAssetBookings({
                status: "approved",
                start: range.start.toISOString(),
                end: range.end.toISOString(),
            });
            const sorted = Array.isArray(res)
                ? [...res].sort((a, b) => new Date(a.start_datetime) - new Date(b.start_datetime))
                : [];
            setOverviewBookings(sorted);
        } catch (e) {
            console.error(e);
            setOverviewError(e);
            setOverviewBookings([]);
        } finally {
            setOverviewLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, [status]);

    useEffect(() => {
        loadOverview(overviewDate);
    }, [overviewDate]);

    const selectedDayBookings = useMemo(
        () => overviewBookings.filter((booking) => bookingTouchesDate(booking, selectedOverviewDate)),
        [overviewBookings, selectedOverviewDate],
    );

    const handleSearch = async (event) => {
        event.preventDefault();
        await load();
    };

    const handleApprove = async (id) => {
        setWorkingId(id);
        setError(null);
        try {
            await approveAssetBooking(id);
            await load();
            await loadOverview(overviewDate);
        } catch (e) {
            console.error(e);
            setError(e);
        } finally {
            setWorkingId(null);
        }
    };

    const handleReject = async (id) => {
        setWorkingId(id);
        setError(null);
        try {
            await rejectAssetBooking(id, rejectNotes[id] || "");
            setRejectNotes((prev) => ({ ...prev, [id]: "" }));
            await load();
            await loadOverview(overviewDate);
        } catch (e) {
            console.error(e);
            setError(e);
        } finally {
            setWorkingId(null);
        }
    };

    return (
        <Container>
            <h1>Materialverleih</h1>

            <Card className="mb-3 asset-admin-overview-card">
                <Card.Body>
                    <div className="asset-admin-overview-header">
                        <div>
                            <h2>Ausleih-Kalender</h2>
                            <div className="asset-admin-overview-subtitle">
                                Freigegebene Ausleihen im {formatMonth(overviewDate)}
                            </div>
                        </div>
                        {overviewLoading ? <Spinner animation="border" size="sm" /> : null}
                    </div>

                    {overviewError && (
                        <Alert variant="danger" className="mt-3">
                            {errorText(overviewError)}
                        </Alert>
                    )}

                    <Row className="g-3">
                        <Col xs={12} lg={5}>
                            <div className="asset-admin-calendar-shell">
                                <Calendar
                                    value={selectedOverviewDate}
                                    onChange={(value) => {
                                        const nextDate = Array.isArray(value) ? value[0] : value;
                                        if (nextDate) setSelectedOverviewDate(nextDate);
                                    }}
                                    onActiveStartDateChange={({ activeStartDate }) => {
                                        if (activeStartDate) setOverviewDate(activeStartDate);
                                    }}
                                    prev2Label={null}
                                    next2Label={null}
                                    tileClassName={({ date, view }) => {
                                        if (view !== "month") return null;
                                        return overviewBookings.some((booking) => bookingTouchesDate(booking, date))
                                            ? "asset-admin-calendar-booked-day"
                                            : null;
                                    }}
                                    tileContent={({ date, view }) => {
                                        if (view !== "month") return null;
                                        const dayBookings = overviewBookings.filter((booking) => bookingTouchesDate(booking, date));
                                        if (!dayBookings.length) return null;
                                        return (
                                            <span className="asset-admin-calendar-count" aria-label={`${dayBookings.length} Ausleihen`}>
                                                {dayBookings.length}
                                            </span>
                                        );
                                    }}
                                />
                            </div>
                        </Col>

                        <Col xs={12} lg={7}>
                            <div className="asset-admin-overview-list-title">
                                Alle Ausleihen im {formatMonth(overviewDate)}
                            </div>
                            {overviewBookings.length === 0 ? (
                                <div className="asset-admin-empty-state">Keine freigegebenen Ausleihen in diesem Monat.</div>
                            ) : (
                                <ListGroup className="asset-admin-overview-list">
                                    {overviewBookings.map((booking) => (
                                        <ListGroup.Item key={`overview-${booking.id}`} className="asset-admin-overview-item">
                                            <div className="asset-admin-overview-row">
                                                <span
                                                    className="asset-admin-overview-swatch"
                                                    style={{ backgroundColor: assetColor(booking.asset) }}
                                                />
                                                <div>
                                                    <div className="asset-admin-overview-name">
                                                        {booking.asset_name} · {booking.member_display_name || "(unbekannt)"}
                                                    </div>
                                                    <div className="asset-admin-overview-time">
                                                        {formatDateTime(booking.start_datetime)} bis {formatDateTime(booking.end_datetime)}
                                                    </div>
                                                    {booking.purpose ? (
                                                        <div className="asset-admin-overview-purpose">{booking.purpose}</div>
                                                    ) : null}
                                                </div>
                                            </div>
                                        </ListGroup.Item>
                                    ))}
                                </ListGroup>
                            )}

                            <div className="asset-admin-selected-day">
                                <div className="asset-admin-overview-list-title">
                                    Ausgewählter Tag: {formatDate(selectedOverviewDate)}
                                </div>
                                {selectedDayBookings.length === 0 ? (
                                    <div className="asset-admin-empty-state">Keine Ausleihe an diesem Tag.</div>
                                ) : (
                                    selectedDayBookings.map((booking) => (
                                        <Badge
                                            key={`day-${booking.id}`}
                                            className="asset-admin-day-badge"
                                            bg="secondary"
                                        >
                                            {booking.asset_name}
                                        </Badge>
                                    ))
                                )}
                            </div>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            <Card className="mb-3">
                <Card.Body>
                    <Form onSubmit={handleSearch}>
                        <Row className="g-2" style={{ alignItems: "end" }}>
                            <Col xs={12} md={6}>
                                <Form.Group>
                                    <Form.Label>Suche</Form.Label>
                                    <Form.Control
                                        value={q}
                                        onChange={(e) => setQ(e.target.value)}
                                        placeholder="Material, Mitglied oder Zweck"
                                    />
                                </Form.Group>
                            </Col>
                            <Col xs={12} md={4}>
                                <Form.Group>
                                    <Form.Label>Status</Form.Label>
                                    <Form.Select value={status} onChange={(e) => setStatus(e.target.value)}>
                                        <option value="pending">Nur ausstehend</option>
                                        <option value="approved">Freigegeben</option>
                                        <option value="rejected">Abgelehnt</option>
                                        <option value="all">Alle</option>
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                            <Col xs={12} md={2}>
                                <Button type="submit" disabled={loading} style={{ width: "100%" }}>
                                    {loading ? "Lade..." : "Suchen"}
                                </Button>
                            </Col>
                        </Row>
                    </Form>

                    {error && (
                        <Alert variant="danger" className="mt-3">
                            {errorText(error)}
                        </Alert>
                    )}
                </Card.Body>
            </Card>

            {loading ? (
                <Spinner animation="border" size="sm" />
            ) : items.length === 0 ? (
                <div style={{ opacity: 0.8 }}>Keine Ausleihen gefunden.</div>
            ) : (
                <ListGroup>
                    {items.map((booking) => (
                        <ListGroup.Item key={booking.id}>
                            <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "flex-start" }}>
                                <div style={{ flex: 1 }}>
                                    <div style={{ fontWeight: 600 }}>
                                        {booking.asset_name} · {booking.member_display_name || "(unbekannt)"}
                                    </div>
                                    <div style={{ opacity: 0.85 }}>
                                        {formatDateTime(booking.start_datetime)} bis {formatDateTime(booking.end_datetime)}
                                    </div>
                                    {booking.member_email ? <div style={{ opacity: 0.75 }}>{booking.member_email}</div> : null}
                                    <div style={{ marginTop: 6 }}>{booking.purpose}</div>
                                    {booking.admin_note ? (
                                        <div style={{ marginTop: 6, opacity: 0.85 }}>Notiz: {booking.admin_note}</div>
                                    ) : null}
                                    {booking.decided_by_username ? (
                                        <div style={{ marginTop: 6, opacity: 0.75 }}>
                                            Entschieden von {booking.decided_by_username}
                                            {booking.decided_at ? ` am ${formatDateTime(booking.decided_at)}` : ""}
                                        </div>
                                    ) : null}
                                </div>

                                <div style={{ minWidth: 220 }}>
                                    <div style={{ textAlign: "right", marginBottom: 8 }}>
                                        <Badge
                                            bg={statusVariant[booking.status] || "secondary"}
                                            text={booking.status === "pending" ? "dark" : undefined}
                                        >
                                            {statusLabel[booking.status] || booking.status}
                                        </Badge>
                                    </div>

                                    {booking.status === "pending" ? (
                                        <div style={{ display: "grid", gap: 8 }}>
                                            <Button
                                                size="sm"
                                                variant="primary"
                                                onClick={() => handleApprove(booking.id)}
                                                disabled={workingId === booking.id}
                                            >
                                                {workingId === booking.id ? "..." : "Freigeben"}
                                            </Button>
                                            <Form.Control
                                                size="sm"
                                                placeholder="Ablehnungsnotiz"
                                                value={rejectNotes[booking.id] || ""}
                                                onChange={(e) =>
                                                    setRejectNotes((prev) => ({ ...prev, [booking.id]: e.target.value }))
                                                }
                                            />
                                            <Button
                                                size="sm"
                                                variant="outline-danger"
                                                onClick={() => handleReject(booking.id)}
                                                disabled={workingId === booking.id}
                                            >
                                                Ablehnen
                                            </Button>
                                        </div>
                                    ) : null}
                                </div>
                            </div>
                        </ListGroup.Item>
                    ))}
                </ListGroup>
            )}
        </Container>
    );
};

export default AssetBookingsAdmin;
